/** 加载 / 错误 / 空三态。
 *
 * 这三态是批次 0 的**核心交付物之一**：后端接口不存在时，界面必须明确说出
 * 「没连上 / 被拒 / 形状不符 / 确实没有数据」，而不是回退成 0 或空表假装业务成功。
 *
 * 分工：
 * - `Loading`：还没结论。给当时在请求什么，别让人盯着转圈猜。
 * - `ErrorState`：给出分类、原始原因与处置建议；可重试的才给「重试」按钮。
 * - `Empty`：**是结论**——请求成功且确实没有记录。文案必须和错误态区分开。
 */

import type { ReactNode } from "react";

import { ApiError, describeError } from "../api/errors";

export function Loading({ what }: { what: string }): ReactNode {
  return (
    <div className="loading" role="status" aria-live="polite">
      <span className="spin" aria-hidden="true" />
      正在从 keel-server 取{what}…
    </div>
  );
}

export function ErrorState({
  error,
  what,
  onRetry,
}: {
  error: unknown;
  what: string;
  onRetry?: () => void;
}): ReactNode {
  const text = describeError(error);
  const retryable = error instanceof ApiError ? error.retryable : false;
  return (
    <div className="errorbox" role="alert">
      <div className="head">
        <span aria-hidden="true">✕</span>
        {text.title} · 取不到{what}
      </div>
      <div className="why">{text.why}</div>
      <div className="hint">{text.hint}</div>
      <div className="acts">
        {retryable && onRetry ? (
          <button type="button" className="btn sm" onClick={onRetry}>
            重试
          </button>
        ) : null}
        {!retryable ? (
          <span className="faint">
            {error instanceof ApiError && error.status === 501 ? "接口落地前重试无用" : "这一类错误重试无用，先修上面那条"}
          </span>
        ) : null}
      </div>
    </div>
  );
}

export function Empty({ what, hint }: { what: string; hint?: ReactNode }): ReactNode {
  return (
    <div className="empty">
      没有{what}
      {hint === undefined ? null : <div className="hint">{hint}</div>}
    </div>
  );
}

/** 屏级取数包装：`pending` → 加载；`error` → 错误；两者都不进业务渲染。 */
export function QueryState({
  status,
  error,
  what,
  refetch,
  children,
}: {
  status: "pending" | "error" | "success";
  error: unknown;
  what: string;
  refetch?: () => void;
  children: ReactNode;
}): ReactNode {
  if (status === "pending") return <Loading what={what} />;
  if (status === "error") return <ErrorState error={error} what={what} onRetry={refetch} />;
  return children;
}
